import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Newsletter from '../components/Newsletter';
import FeaturedArticle from '../components/FeaturedArticle';
import NewsCard from '../components/NewsCard';
import ThemeFilter from '../components/ThemeFilter';
import { news, desks } from '../mock';

// `news` is a static import, so themes can be derived once at module load.
const themes = ['All themes', ...news.reduce((acc, n) => {
  if (n.theme && !acc.includes(n.theme)) acc.push(n.theme);
  return acc;
}, [])];

const AuthorStories = ({ authorName }) => {
  const stories = news.filter((n) => n.author && n.author.name === authorName);
  const author = stories[0]?.author;

  return (
    <div>
      <section className="pt-[140px] pb-10 px-6 lg:px-10">
        <div className="max-w-[1400px] mx-auto fade-up">
          <Link to="/news" data-testid="back-to-all-news" className="inline-flex items-center gap-2 text-[#9aa3b2] hover:text-white text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> All news
          </Link>
          <div className="flex items-center gap-5 mt-6">
            {author?.avatar && (
              <img src={author.avatar} alt={authorName} className="w-20 h-20 rounded-full object-cover object-top border border-white/10" />
            )}
            <div>
              <span className="dctv-eyebrow text-[#e8743b]">Stories by</span>
              <h1 className="font-serif-dctv text-4xl md:text-5xl font-bold text-white mt-1">{authorName}</h1>
              {author?.role && <p className="text-[#9aa3b2] text-sm mt-2">{author.role}</p>}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {stories.map((n) => <NewsCard key={n.id} article={n} />)}
          </div>
          {stories.length === 0 && (
            <p className="text-[#9aa3b2] text-center py-16">No stories from this author yet - check back soon.</p>
          )}
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

const DeskStories = ({ deskId }) => {
  const desk = desks.find((d) => d.id === deskId);
  const stories = news.filter((n) => n.desk === deskId);

  return (
    <div>
      <section className="pt-[140px] pb-10 px-6 lg:px-10">
        <div className="max-w-[1400px] mx-auto fade-up">
          <Link to="/news" data-testid="back-to-all-news" className="inline-flex items-center gap-2 text-[#9aa3b2] hover:text-white text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> All news
          </Link>
          <span className="dctv-eyebrow text-[#e8743b] block mt-6">{desk?.eyebrow || 'Desk'}</span>
          <h1 className="font-serif-dctv text-4xl md:text-6xl font-bold text-white mt-2">{desk?.label || 'Desk'}</h1>
          {desk?.blurb && <p className="text-[#bcc3cf] text-lg leading-relaxed mt-6 max-w-2xl">{desk.blurb}</p>}
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {stories.map((n) => <NewsCard key={n.id} article={n} />)}
          </div>
          {stories.length === 0 && (
            <p className="text-[#9aa3b2] text-center py-16">No stories from this desk yet - check back soon.</p>
          )}
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

const DeskBand = ({ desk }) => {
  const stories = news.filter((n) => n.desk === desk.id).slice(0, 3);
  if (stories.length === 0) return null;

  return (
    <section className="px-6 lg:px-10 pb-12" data-testid={`desk-band-${desk.id}`}>
      <div className="max-w-[1400px] mx-auto rounded-3xl border border-[#e8743b]/25 bg-gradient-to-br from-[#e8743b]/10 via-[#0c1119] to-[#0c1119] p-8 lg:p-10 fade-up">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7">
          <div>
            <span className="dctv-eyebrow text-[#e8743b]">{desk.eyebrow}</span>
            <h2 className="font-serif-dctv text-3xl md:text-4xl font-bold text-white mt-2">{desk.label}</h2>
            <p className="text-[#bcc3cf] text-sm md:text-base leading-relaxed mt-3 max-w-2xl">{desk.blurb}</p>
          </div>
          <Link
            to={`/news?desk=${desk.id}`}
            data-testid={`desk-view-all-${desk.id}`}
            className="inline-flex items-center gap-2 text-[#f4965a] hover:text-white text-sm font-medium whitespace-nowrap transition-colors"
          >
            View desk <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {stories.map((n) => <NewsCard key={n.id} article={n} />)}
        </div>
      </div>
    </section>
  );
};

const News = () => {
  const [params] = useSearchParams();
  const authorName = params.get('author');
  const deskId = params.get('desk');
  const [active, setActive] = useState('All themes');

  if (authorName) return <AuthorStories authorName={authorName} />;
  if (deskId) return <DeskStories deskId={deskId} />;

  const featured = news.find((n) => n.featured);
  const rest = news.filter((n) => !n.featured);
  const filtered = active === 'All themes' ? rest : rest.filter((n) => n.theme === active);

  return (
    <div>
      <section className="pt-[140px] pb-10 px-6 lg:px-10">
        <div className="max-w-[1400px] mx-auto fade-up">
          <span className="dctv-eyebrow text-[#e8743b]">News &amp; blog</span>
          <h1 className="font-serif-dctv text-5xl md:text-6xl font-bold text-white mt-5">Latest from the community</h1>
          <p className="text-[#bcc3cf] text-lg leading-relaxed mt-6 max-w-2xl">
            Reporting across our programme themes - from migration and integration to gender, culture, business and human rights, in the Netherlands, China and the wider diaspora.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-10">
        <div className="max-w-[1400px] mx-auto"><FeaturedArticle article={featured} /></div>
      </section>

      {desks.map((d) => <DeskBand key={d.id} desk={d} />)}

      <section className="px-6 lg:px-10 pb-8">
        <div className="max-w-[1400px] mx-auto">
          <ThemeFilter themes={themes} active={active} onChange={setActive} />
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((n) => <NewsCard key={n.id} article={n} />)}
          </div>
          {filtered.length === 0 && (
            <p className="text-[#9aa3b2] text-center py-16">No stories under this theme yet - check back soon.</p>
          )}
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default News;
